import React, { useEffect, useRef } from "react"
import QRCodeStyling from "qr-code-styling"
import dayjs from "dayjs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/es"
import { capitalizeNombresPropios } from "../../utils/StringUtils"
import { QrConfig, QrData } from "./qrConfigs"

dayjs.locale("es")
dayjs.extend(localizedFormat)

interface FiestaQRCardProps {
  config: QrConfig
  data: QrData
  cardWidth?: number
  qrSize?: number
}

// Dimensiones base: la imagen es 1047×1501
// Recuadro blanco ocupa ~50% del ancho, centrado, comienza ~11% desde arriba y dura ~36%

const FiestaQRCard: React.FC<FiestaQRCardProps> = ({ config, data, cardWidth = 260, qrSize = 135 }) => {
  const qrRef = useRef<HTMLDivElement>(null)
  const qrValue = config.suffix ? `${data.qrBase}${config.suffix}` : data.qrBase
  const scale = cardWidth / 260

  const cantidadTarjetas =
    config.campoTarjetas === "tarjetasPagadasMenores"
      ? data.tarjetasPagadasMenores
      : data.tarjetasPagadas

  const formattedDate = dayjs(data.fechaEvento).format("dddd D-MM")
  const dateLabel = formattedDate.toUpperCase()

  useEffect(() => {
    if (!qrRef.current) return
    qrRef.current.innerHTML = ""

    const qr = new QRCodeStyling({
      width: qrSize,
      height: qrSize,
      data: qrValue,
      type: "svg",
      margin: 0,
      imageOptions: { hideBackgroundDots: true, imageSize: 0.4, margin: 0 },
      qrOptions: config.qrOptions,
      dotsOptions: config.dotsOptions,
      cornersSquareOptions: config.cornersSquareOptions,
      cornersDotOptions: config.cornersDotOptions,
      backgroundOptions: config.backgroundOptions,
    })

    qr.append(qrRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrValue])

  return (
    <div
      style={{
        position: "relative",
        width: cardWidth,
        maxWidth: "100%",
        margin: "0 auto 16px",
        userSelect: "none",
      }}
    >
      <img
        src="/assets/img/fondo_qr_retro.jpg"
        alt="Fondo fiesta"
        style={{ width: "100%", display: "block", borderRadius: 10 }}
        draggable={false}
      />

      {/* Fecha y horario — sobre el área oscura superior */}
      <div
        style={{
          position: "absolute",
          top: "2.8%",
          left: "8%",
          right: "8%",
          textAlign: "center",
          color: "#ffb347",
          fontWeight: 700,
          textShadow: "0 1px 6px rgba(0,0,0,0.9)",
          lineHeight: 1.4,
        }}
      >
        <div style={{ fontSize: Math.round(13 * scale), letterSpacing: "0.3px" }}>{dateLabel}</div>
        {config.horario && (
          <div style={{ fontSize: Math.round(11 * scale), fontWeight: 600 }}>{config.horario}</div>
        )}
      </div>

      {/* QR — centrado dentro del recuadro blanco */}
      <div
        style={{
          position: "absolute",
          top: "14%",
          left: "50%",
          transform: "translateX(-50%)",
          height: "36%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div ref={qrRef} />
      </div>

      {/* Nombre y tarjetas — sobre el área inferior */}
      {(() => {
        const fullName = `${capitalizeNombresPropios(data.nombre)} ${capitalizeNombresPropios(data.apellido)}`
        const textStyle: React.CSSProperties = {
          color: "#ffb347",
          fontWeight: 700,
          fontSize: Math.round(12 * scale),
          textShadow: "0 1px 5px rgba(0,0,0,0.95)",
          lineHeight: 1.4,
        }
        return (
          <div
            style={{
              position: "absolute",
              bottom: "2.5%",
              left: "5%",
              right: "5%",
              textAlign: "center",
            }}
          >
            {fullName.length <= 20 ? (
              <>
                <div style={textStyle}>
                  {fullName} - {cantidadTarjetas} pers.
                </div>
                {data.dni && <div style={textStyle}>DNI: {data.dni}</div>}
              </>
            ) : (
              <>
                <div style={textStyle}>{fullName}</div>
                <div style={textStyle}>
                  {data.dni ? `DNI: ${data.dni} - ` : ""}{cantidadTarjetas} pers.
                </div>
              </>
            )}
          </div>
        )
      })()}
    </div>
  )
}

export default FiestaQRCard
